import { Box, Tooltip, Typography } from "@mui/material";
import { SxProps } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  colorAccent,
  colorAccentHover,
  colorTextDisabled,
  colorTextMuted,
  colorTextSecondary,
  radiusChip,
} from "src/design/tokens";

/** One bar: a category and the value it stands for. */
export interface MiniBarDatum {
  /** Label printed under the bar, e.g. "20h". */
  label: string;
  value: number;
}

interface MiniBarChartProps {
  data: MiniBarDatum[];
  /** Optional caption above the strip. */
  title?: string;
  /**
   * Total height of the strip in pixels, labels included — so a caller can
   * match it to a sibling chart without knowing how the strip is built.
   */
  height?: number;
  /** Darkens the tallest bar(s) so the peak reads at a glance. */
  emphasisePeak?: boolean;
  /** Builds the hover label; defaults to the raw value. */
  formatValue?: (datum: MiniBarDatum) => string;
  sx?: SxProps;
}

const DEFAULT_HEIGHT = 160;

/**
 * Height reserved for the label row: its line box plus the gap above it. Pinned
 * rather than left to the font so `height` stays the strip's true total.
 */
const LABEL_LINE_HEIGHT = 16;
const LABEL_GAP = 6;
const LABEL_ROW_HEIGHT = LABEL_LINE_HEIGHT + LABEL_GAP;

/**
 * Bars share the width evenly but stop widening past this, centred in their
 * column. A short series would otherwise render as slabs that read as blocks of
 * colour rather than as a distribution.
 */
const MAX_BAR_WIDTH = 28;

/**
 * Shortest a non-zero bar may be drawn, as a share of the bar area. Without a
 * floor a value far below the peak collapses into an invisible sliver and reads
 * as "nothing happened" rather than "a little happened".
 */
const MIN_VISIBLE_RATIO = 0.04;

/**
 * A compact bar strip with no axes, grid or legend — just the shape of a
 * distribution and its peak. Meant for a dashboard tile where a full
 * `BarChart` would be too heavy; reach for `BarChart` when readers need to
 * compare exact values off an axis.
 */
export const MiniBarChart = ({
  data,
  title,
  height = DEFAULT_HEIGHT,
  emphasisePeak = true,
  formatValue,
  sx,
}: MiniBarChartProps) => {
  const { t } = useTranslation();

  const peak = data.reduce((max, datum) => Math.max(max, datum.value), 0);
  const isEmpty = data.length === 0 || peak === 0;
  const barAreaHeight = Math.max(height - LABEL_ROW_HEIGHT, 0);

  return (
    <Box sx={{ width: "100%", ...sx }}>
      {title && (
        <Typography sx={{ fontSize: "12.5px", color: colorTextMuted, mb: 1 }}>
          {title}
        </Typography>
      )}

      {isEmpty ? (
        <Box
          sx={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: "13px", color: colorTextMuted }}>
            {t("common.noData")}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.75 }}>
          {data.map((datum, index) => {
            // Ties all count as the peak: silently picking the first would
            // claim a winner the data does not have.
            const isPeak = emphasisePeak && datum.value === peak;
            const ratio = datum.value / peak;

            return (
              <Box
                key={`${datum.label}-${index}`}
                sx={{ flex: 1, minWidth: 0, textAlign: "center" }}
              >
                <Tooltip
                  title={formatValue ? formatValue(datum) : String(datum.value)}
                  placement="top"
                  arrow
                >
                  <Box
                    sx={{
                      height: barAreaHeight,
                      display: "flex",
                      flexDirection: "column-reverse",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: MAX_BAR_WIDTH,
                        height: `${
                          (datum.value === 0
                            ? 0
                            : Math.max(ratio, MIN_VISIBLE_RATIO)) * 100
                        }%`,
                        backgroundColor: isPeak ? colorAccentHover : colorAccent,
                        borderRadius: radiusChip,
                      }}
                    />
                  </Box>
                </Tooltip>
                <Typography
                  sx={{
                    fontSize: "10.5px",
                    lineHeight: `${LABEL_LINE_HEIGHT}px`,
                    marginTop: `${LABEL_GAP}px`,
                    color: isPeak ? colorTextSecondary : colorTextDisabled,
                    fontWeight: isPeak ? 600 : 400,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {datum.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
